import useGetMenuLogo from '@/hooks/useGetMenuLogo'
import { generateImageSources } from '@/utils/uploadcare'
import Image from 'next/image'
import { cn } from 'oddle-shop-monorepo.design-system'
import React from 'react'
import { H2 } from '@/components/common/Typo'

interface HeaderLogoProps {
  isMobile?: boolean
  truncate?: boolean
  showStoreName?: boolean
  className?: string
  logoClassName?: string
  /**
   * Both variants reserve a box whose size never depends on the loaded
   * image (that dependency is what shifted layout: with `width: auto` the
   * browser only learns the real logo width after the image loads — 0.254
   * CLS, measured on both the header and the image-with-text placeholder).
   *
   * - reserveBox (page-flow header): explicit 135x44 / 180x60 box,
   *   object-left so the logo hugs the header's left edge.
   * - default (placeholder/avatar usages): same box but capped by the
   *   container (max-w-full) and object-center, so the logo stays centred
   *   inside fixed-size cards (product fallback, image-with-text
   *   placeholder, toasts…) without ever resizing on load.
   */
  reserveBox?: boolean
}

const HeaderLogo: React.FC<HeaderLogoProps> = ({
  isMobile,
  truncate = false,
  showStoreName: showStoreNameProps,
  className = '',
  logoClassName = '',
  reserveBox = false,
}) => {
  const { logoImage, showLogo, showStoreName, storeName } = useGetMenuLogo()
  const { src } = generateImageSources({ url: logoImage, baseW: 240 })
  const showStoreNamePropsFinal =
    showStoreNameProps === undefined ? showStoreName : showStoreNameProps

  return (
    <>
      {/* Desktop Version */}
      <div className={cn('hidden md:inline', className)}>
        <div className={cn('flex items-center', truncate ? '' : 'ml-3.5')}>
          {showLogo && src && (
            <Image
              alt={storeName}
              src={src}
              width={135}
              height={44}
              className={cn(
                reserveBox
                  ? 'max-w-[135px] object-contain object-left md:h-[60px] md:w-[180px] md:max-w-[180px]'
                  : 'h-[44px] w-[135px] max-w-full object-contain object-center md:h-[60px] md:w-[180px]',
                truncate ? '' : 'mr-3 md:ml-3',
                logoClassName,
              )}
              unoptimized={false}
            />
          )}
          {showStoreNamePropsFinal && !isMobile && (
            <H2 className="text-header-link-color mr-3 font-bold leading-tight">
              {storeName}
            </H2>
          )}
        </div>
      </div>

      {/* Mobile Version */}
      <div className={cn('md:hidden', className)}>
        {showLogo && src ? (
          <>
            <Image
              alt={storeName}
              src={src}
              width={135}
              height={44}
              className={cn(
                reserveBox
                  ? 'mr-3 h-[44px] max-h-[44px] w-[135px] object-contain object-left'
                  : 'mr-3 h-[44px] max-h-[44px] w-[135px] max-w-full object-contain object-center',
                logoClassName,
              )}
              unoptimized={false}
            />
            {showStoreNameProps && (
              <H2 className="text-header-link-color mb-7 mt-2.5 max-w-[161px] font-bold leading-8">
                {storeName}
              </H2>
            )}
          </>
        ) : (
          showStoreNamePropsFinal && (
            <H2 className="text-header-link-color max-w-[161px] font-bold leading-tight">
              {storeName}
            </H2>
          )
        )}
      </div>
    </>
  )
}

export default HeaderLogo
